import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { Surface } from '@/components/ui/surface'
import { cn } from '@/lib/utils'

export interface StatTileProps {
  label: string
  value: number | string
  /** What the number counts, in one line (U8 style). */
  definition: string
  /** `danger` tints the tile and adds a "!" for something that is overdue. */
  tone?: 'default' | 'danger'
  /** The tile becomes a link with the accessible name "label: value". */
  to?: string
  /** The tile becomes a button (for a tile that acts on the page itself). */
  onClick?: () => void
  /** Extra lines under the value, e.g. "11 personen · 3 org.". */
  subParts?: ReactNode[]
  className?: string
}

/**
 * A key figure: label, big value, definition (and sub parts). With `to` the whole tile is a link
 * named "label: value", with `onClick` a button; numbers are `tabular-nums`.
 */
export function StatTile({ label, value, definition, tone = 'default', to, onClick, subParts, className }: StatTileProps) {
  const danger = tone === 'danger' && Number(value) > 0
  const body = (
    <>
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className="flex items-baseline gap-2">
        <span className={cn('font-heading text-3xl font-semibold tabular-nums', danger ? 'text-danger' : 'text-foreground')}>
          {value}
        </span>
        {danger ? (
          <span aria-hidden className="font-semibold text-danger">
            !
          </span>
        ) : null}
      </span>
      {subParts && subParts.length > 0 ? (
        <span className="text-sm text-foreground">
          {subParts.map((part, index) => (
            <span key={index}>
              {index > 0 ? ' · ' : null}
              {part}
            </span>
          ))}
        </span>
      ) : null}
      <span className="text-xs text-muted-foreground">{definition}</span>
    </>
  )
  const classes = cn(
    'flex h-full min-w-0 flex-col gap-1',
    danger && 'border-danger',
    (to || onClick) && 'transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring',
    className,
  )
  const name = `${label}: ${value}`
  if (to) {
    return (
      <Surface asChild variant="section" className={classes}>
        <Link to={to} aria-label={name} data-slot="stat-tile">
          {body}
        </Link>
      </Surface>
    )
  }
  if (onClick) {
    return (
      <Surface asChild variant="section" className={cn(classes, 'text-left')}>
        <button type="button" onClick={onClick} aria-label={name} data-slot="stat-tile">
          {body}
        </button>
      </Surface>
    )
  }
  return (
    <Surface variant="section" className={classes} data-slot="stat-tile" role="group" aria-label={name}>
      {body}
    </Surface>
  )
}
